import { Box, Typography, Slider, Divider, FormHelperText } from '@mui/material';
import type { PriorityWeights } from '../RulesConfigurationDialog';

export interface PriorityWeightsTabProps {
  weights: PriorityWeights;
  onChange: (weights: PriorityWeights) => void;
}

export function PriorityWeightsTab({ weights, onChange }: PriorityWeightsTabProps) {
  const handleChange = (field: keyof PriorityWeights, value: number) => {
    onChange({ ...weights, [field]: value });
  };

  const marks = [
    { value: 0, label: '0' },
    { value: 5, label: '5' },
    { value: 10, label: '10' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure the importance of each factor in the teacher suggestion and auto-fill
          algorithm. Higher values mean the factor has more influence on ranking.
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Workload Balance
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Prefer teachers with fewer assigned periods to distribute work evenly
        </Typography>
        <Slider
          value={weights.workloadBalance}
          onChange={(_, v) => handleChange('workloadBalance', v as number)}
          min={0} max={10} marks={marks} valueLabelDisplay="auto" sx={{ mt: 2 }}
        />
        <FormHelperText>Current: {weights.workloadBalance}/10</FormHelperText>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Teacher-Class Association
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Prefer teachers explicitly linked to this class via Teacher-Class Assignments in Master Data
        </Typography>
        <Slider
          value={weights.teacherClassAssociation}
          onChange={(_, v) => handleChange('teacherClassAssociation', v as number)}
          min={0} max={10} marks={marks} valueLabelDisplay="auto" sx={{ mt: 2 }}
        />
        <FormHelperText>Current: {weights.teacherClassAssociation}/10</FormHelperText>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Subject Qualification
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Prefer teachers whose subject assignments (in Master Data) include this subject
        </Typography>
        <Slider
          value={weights.subjectQualification}
          onChange={(_, v) => handleChange('subjectQualification', v as number)}
          min={0} max={10} marks={marks} valueLabelDisplay="auto" sx={{ mt: 2 }}
        />
        <FormHelperText>Current: {weights.subjectQualification}/10 — recommended: 10</FormHelperText>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Subject Continuity
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Prefer teachers already teaching this subject to this section — keeps the same teacher
          across all periods of a subject for a class
        </Typography>
        <Slider
          value={weights.subjectContinuity}
          onChange={(_, v) => handleChange('subjectContinuity', v as number)}
          min={0} max={10} marks={marks} valueLabelDisplay="auto" sx={{ mt: 2 }}
        />
        <FormHelperText>Current: {weights.subjectContinuity}/10</FormHelperText>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Avoid Consecutive Periods
        </Typography>
        <Typography variant="caption" color="text.secondary" paragraph>
          Penalise teachers who already have back-to-back periods — works together with
          Max Consecutive Periods in Teacher Rules
        </Typography>
        <Slider
          value={weights.avoidConsecutive}
          onChange={(_, v) => handleChange('avoidConsecutive', v as number)}
          min={0} max={10} marks={marks} valueLabelDisplay="auto" sx={{ mt: 2 }}
        />
        <FormHelperText>Current: {weights.avoidConsecutive}/10</FormHelperText>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 2,
          backgroundColor: 'info.light',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.main',
        }}
      >
        <Typography variant="caption" color="info.dark">
          <strong>Recommended:</strong> Subject Qualification (10), Subject Continuity (8),
          Teacher-Class Association (8), Avoid Consecutive (4), Workload Balance (5).
        </Typography>
      </Box>
    </Box>
  );
}
